var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { boundMethod } from 'autobind-decorator';
import { Container, Sprite, } from 'pixi.js';
import { createElement } from 'react';
import { TooltipContent } from 'flow-builder/components/connectorTooltip/connectorTooltip';
import { HighlightType } from '../../types/node';
import { getIsNodeHighlighted } from '../../utils/node/getIsNodeHighlighted/getIsNodeHighlighted';
import { createTextureSpriteMap, setHighlightSpriteVisibility, } from '../utils/highlightTextureMap';
import { styles } from './nodeArrowStyles';
import { styles as nodeStyles } from './nodeStyles';
export class NodeArrow {
    static InitializeTextureSprite(textureMap, name) {
        return Object.fromEntries(Object.entries(textureMap).map(([type, texture]) => {
            const sprite = new Sprite(texture);
            sprite.visible = false;
            sprite.anchor.set(0.5, 0.5);
            sprite.name = `${name} (${type})`;
            return [type, sprite];
        }));
    }
    constructor(props) {
        this.isLocked = false;
        this.isHighlighted = false;
        this.props = props;
        const { arrowTextures } = this.props;
        const ref = new Container();
        const arrow = new Container();
        const disconnect = new Container();
        const locked = new Container();
        this._arrowTypes = createTextureSpriteMap(arrowTextures.arrow, 'Arrow');
        this._disconnectTypes = createTextureSpriteMap(arrowTextures.disconnect, 'Disconnect');
        this._lockedTypes = createTextureSpriteMap(arrowTextures.locked, 'Locked');
        ref.position.set(nodeStyles.base.width / 2, 0);
        const addSpriteToContainer = (sprite, container) => container.addChild(sprite);
        const addSpriteTypes = (types, container) => Object.values(types).forEach((sprite) => addSpriteToContainer(sprite, container));
        addSpriteTypes(this._arrowTypes, arrow);
        addSpriteTypes(this._disconnectTypes, disconnect);
        addSpriteTypes(this._lockedTypes, locked);
        const assignContainerAttributes = (container) => {
            container.interactive = true;
            container.buttonMode = true;
        };
        assignContainerAttributes(arrow);
        assignContainerAttributes(disconnect);
        assignContainerAttributes(locked);
        disconnect.visible = false;
        locked.visible = false;
        ref.addChild(arrow);
        ref.addChild(disconnect);
        ref.addChild(locked);
        this.ref = ref;
        this.arrow = arrow;
        this.disconnect = disconnect;
        this.locked = locked;
        this.isLocked = this.getIsLocked();
        this.isHighlighted = this.getIsHighlighted();
        this.drawArrowHighlight();
        this.drawArrowVisibility();
        this.addDisconnectListener();
    }
    getIsLocked() {
        const { node: { id }, editorContext: { state: { nodeState: { scopeVars }, }, }, } = this.props;
        return Object.values(scopeVars).some(({ ancestors }) => ancestors && ancestors.indexOf(id) >= 0);
    }
    getIsHighlighted() {
        const { node, editorContext: { state: { highlightState }, }, } = this.props;
        return getIsNodeHighlighted(node.id, highlightState.nodes, true);
    }
    mouseOutListener(event) {
        const { editorContext: { view }, } = this.props;
        if (!view.isReady) {
            return;
        }
        const arrow = this.arrow;
        const disconnect = this.disconnect;
        const locked = this.locked;
        const { plugins } = view.app.renderer;
        const interaction = plugins.interaction;
        const point = event.data.global;
        const target = this.isLocked ? locked : disconnect;
        const isHoveringElement = !!interaction.hitTest(point, target);
        if (!isHoveringElement) {
            if (this.isLocked) {
                this.onLockOut();
            }
            target.visible = false;
            arrow.visible = true;
            this.removeMouseOutListener();
        }
    }
    handleMouseOutListener(method) {
        const { editorContext: { view }, } = this.props;
        if (!view.isReady) {
            return;
        }
        view.viewport[method]('mousemove', this.mouseOutListener);
    }
    addMouseOutListener() {
        this.handleMouseOutListener('addListener');
    }
    removeMouseOutListener() {
        this.handleMouseOutListener('removeListener');
    }
    addDisconnectListener() {
        const arrow = this.arrow;
        const disconnect = this.disconnect;
        const locked = this.locked;
        arrow.on('mouseover', () => {
            if (this.isLocked) {
                locked.visible = true;
            }
            else {
                disconnect.visible = true;
            }
            arrow.visible = false;
            this.addMouseOutListener();
        });
        locked.on('click', this.onLockClick);
        disconnect.on('click', this.onDisconnectClick);
    }
    onLockClick(event) {
        event.stopPropagation();
        const { node: { id }, editorContext, } = this.props;
        const { state: { nodeState: { scopeVars }, }, handlers: { ui: { onTooltipShow }, }, } = editorContext;
        const { target } = event;
        const { a, tx, ty } = target.worldTransform;
        const requiredScopeVar = Object.entries(scopeVars).find(([_id, { ancestors }]) => ancestors && ancestors.indexOf(id) >= 0);
        if (!requiredScopeVar) {
            return;
        }
        const [dependantId, { needs }] = requiredScopeVar;
        const isDependant = dependantId === id;
        onTooltipShow({
            head: 'Attention',
            content: createElement(TooltipContent, { isDependant, needs }),
            position: {
                x: tx,
                y: ty - styles._lockedCircle.radius * a,
            },
        });
    }
    onLockOut() {
        const { editorContext: { handlers: { ui: { onTooltipHide }, }, }, } = this.props;
        onTooltipHide();
    }
    onDisconnectClick(event) {
        event.stopPropagation();
        this.removeMouseOutListener();
        const container = this.disconnect;
        const { node: { id }, editorContext: { handlers: { node: { onDisconnect }, }, }, } = this.props;
        const disconnect = onDisconnect(id);
        container.visible = false;
        disconnect();
    }
    drawArrowVisibility() {
        const { node: { parentId }, } = this.props;
        const arrow = this.arrow;
        if (!parentId) {
            arrow.visible = false;
            return;
        }
        arrow.visible = true;
    }
    drawArrowHighlight() {
        const { editorContext } = this.props;
        const { state: { highlightState }, } = editorContext;
        const activeHighlightType = this.isHighlighted
            ? highlightState.type
            : HighlightType.NONE;
        setHighlightSpriteVisibility(this._arrowTypes, activeHighlightType);
        setHighlightSpriteVisibility(this._disconnectTypes, activeHighlightType);
        setHighlightSpriteVisibility(this._lockedTypes, activeHighlightType);
    }
    isChanged(props) {
        const { node, node: { parentId }, editorContext: { state: { highlightState }, }, } = props;
        const { node: propNode, node: { parentId: propsParentId }, editorContext: { state: { highlightState: propHighlightState }, }, } = this.props;
        const isHighlighted = getIsNodeHighlighted(node.id, highlightState.nodes, true);
        const propIsHighlighted = getIsNodeHighlighted(propNode.id, propHighlightState.nodes, true);
        const isNodeHighlightDifferent = isHighlighted !== propIsHighlighted;
        const isParentIdDifferent = parentId !== propsParentId;
        return isNodeHighlightDifferent || isParentIdDifferent;
    }
    redraw(props) {
        this.updatePropsWithoutRedraw(props);
        this.drawArrowHighlight();
        this.drawArrowVisibility();
    }
    updatePropsWithoutRedraw(props) {
        this.props = props;
        this.isLocked = this.getIsLocked();
        this.isHighlighted = this.getIsHighlighted();
    }
    unmount() {
        const container = this.ref;
        container.destroy();
    }
}
__decorate([
    boundMethod
], NodeArrow.prototype, "mouseOutListener", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "onLockClick", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "onLockOut", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "onDisconnectClick", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "redraw", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "updatePropsWithoutRedraw", null);
__decorate([
    boundMethod
], NodeArrow.prototype, "unmount", null);
